import { pushDataLayerEvent } from '../consent/services/gtm'

const FIRED_KEY_PREFIX = 'ads_purchase_fired:'
const DEFAULT_CURRENCY = 'PLN'
const ADS_CONVERSION_SEND_TO = 'AW-18207194241/slg9CObz3bccEIH57ulD'

/** How long to wait for async gtag.js if the stub is missing (ms). */
const GTAG_RETRY_INTERVAL_MS = 250
const GTAG_RETRY_MAX_ATTEMPTS = 20

export type AdsPurchasePayload = {
  transactionId: string
  value: number
  currency: string
}

function alreadyFired(transactionId: string): boolean {
  try {
    return sessionStorage.getItem(`${FIRED_KEY_PREFIX}${transactionId}`) === '1'
  } catch {
    return false
  }
}

function markFired(transactionId: string): void {
  try {
    sessionStorage.setItem(`${FIRED_KEY_PREFIX}${transactionId}`, '1')
  } catch {
    // sessionStorage may be unavailable — still push once this page load
  }
}

function isGtagCallable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function'
}

/**
 * Fire Google Ads conversion via gtag. The index.html stub always queues into
 * dataLayer, so this usually succeeds immediately; retry covers rare cases
 * where purchase fires before the head stub (or gtag was stripped).
 */
function fireGtagAdsConversion(args: {
  transactionId: string
  value: number
  currency: string
}): void {
  const payload = {
    send_to: ADS_CONVERSION_SEND_TO,
    value: args.value,
    currency: args.currency,
    transaction_id: args.transactionId,
  }

  const tryFire = (): boolean => {
    if (!isGtagCallable()) return false
    window.gtag!('event', 'conversion', payload)
    return true
  }

  if (tryFire()) return

  let attempts = 0
  const timer = window.setInterval(() => {
    attempts += 1
    if (tryFire() || attempts >= GTAG_RETRY_MAX_ATTEMPTS) {
      window.clearInterval(timer)
    }
  }, GTAG_RETRY_INTERVAL_MS)
}

/**
 * Push Google Ads purchase via GTM dataLayer (event: ads_purchase) and
 * directly via gtag conversion (AW-18207194241 / slg9CObz3bccEIH57ulD).
 * Idempotent per transaction_id in the current tab.
 *
 * GTM (GTM-5S39LB6): Ads Conversion tag on Custom Event ads_purchase;
 * map transaction_id, value, currency.
 */
export function trackAdsPurchaseConversion(payload: AdsPurchasePayload): void {
  const transactionId = payload.transactionId?.trim()
  if (!transactionId) return
  if (alreadyFired(transactionId)) return

  const value = payload.value
  const currency = payload.currency || DEFAULT_CURRENCY

  markFired(transactionId)
  pushDataLayerEvent({
    event: 'ads_purchase',
    transaction_id: transactionId,
    value,
    currency,
  })
  fireGtagAdsConversion({ transactionId, value, currency })
}

export type CheckoutStatusConversion = {
  status?: string
  is_first_purchase?: boolean | null
  transaction_id?: string | null
  value?: number | null
  currency?: string | null
}

/** Fire Ads conversion only for a completed first purchase. */
export function maybeTrackAdsPurchaseFromCheckoutStatus(
  data: CheckoutStatusConversion,
): void {
  if (data.status !== 'completed') return
  if (data.is_first_purchase !== true) return
  if (!data.transaction_id) return

  trackAdsPurchaseConversion({
    transactionId: data.transaction_id,
    value: typeof data.value === 'number' ? data.value : 0,
    currency: data.currency || DEFAULT_CURRENCY,
  })
}
